import React, { useContext, useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Popup } from 'react-map-gl'
import ImrayLogo from '../../assets/img/brand/Imray_Logo_2010.png'
import { AppContext } from '../../context'
import { conjurePromises } from '../../utils/mapHelpers'
// create the popup
// offset to cluster radius

interface LocationPopupProps {
  onClose: () => void
  lng: number
  lat: number
  features: any[]
  matchingName: string
  focusOnLocation: (id: string, layer: string) => void
}

interface BookData {
  id: string
  bookCoverURL: string
  bookAuthors: string[]
  bookName: string
}

const LocationPopup: React.FC<LocationPopupProps> = ({
  onClose,
  lng,
  lat,
  features,
  matchingName,
  focusOnLocation
}) => {
  const {
    setDetailsOverlayData,
    activePopover,
    onTogglePopover,
    userSubscriptionStatus
  } = useContext(AppContext)
  const isSubscribed =
    userSubscriptionStatus &&
    (userSubscriptionStatus?.hasSubscription ||
      userSubscriptionStatus?.trialExpiresIn > 0)

  const [booksData, setBooksData] = useState<BookData[]>([])
  const [booksDataLoaded, setBooksDataLoaded] = useState(false)

  const featureIds = features.map(f => f.properties.id)

  const fetchMultipleUrls = async (ids: string[]) => {
    const promises = await conjurePromises(ids, {
      method: 'GET',
      endpoint: '/content'
    })

    const data: BookData[] = promises.map(promise => promise.data.message)
    setBooksData(data)
  }

  const viewSelectedPlace = (id: string) => {
    if (!booksData || !id) return

    const placeProps: any = booksData.find(v => v.id === id)

    setDetailsOverlayData({
      props: placeProps,
      propsWithoutLinks: placeProps,
      hasAccess: isSubscribed as boolean
    })

    if (activePopover !== 'place-detailed') {
      onTogglePopover('place-detailed')
    }
  }

  useEffect(() => {
    setBooksDataLoaded(false)

    const fetch = async () => {
        try {
        await fetchMultipleUrls(featureIds)
        setBooksDataLoaded(true)
      } catch (error) {
        setBooksDataLoaded(true)
        console.log(`Error posting comment: ${error}`)
      }
    }

    fetch().then()
  }, [])

  if (!booksDataLoaded) {
    return null
  }

  return (
    <Popup
      longitude={lng}
      latitude={lat}
      anchor="bottom"
      closeOnClick={false}
      closeButton={false}
      maxWidth="320px"
            offset={isMobile ? 970 : 1050}
    >
      <div className="popup-header">
        <a
          className="close-button btn-pill no-fill no-label"
          href="#"
          onClick={e => {
            if (e.preventDefault) e.preventDefault()
            onClose()
          }}
        >
          <span className="material-icons">clear</span>
        </a>
      </div>
      <h4>{matchingName ?? 'Multiple locations'}</h4>
      <div>
        <p>
          This location appears in different books,
          <br />
          please select a book below:
        </p>
      </div>
      <div>
        {features.map(f => {
          const bookId = f.properties.id
          const bookData = booksData?.find(b => b.id === bookId) ?? null
          const authorImageURL = bookData?.bookCoverURL ?? ImrayLogo
          const author = booksData.find(a => a.id === bookId)?.bookAuthors

          if (!bookData) return null

          return (
            <div
              key={`${f.properties.id}`}
              className="locationbookrow select-feature"
              onClick={e => {
                const locationId = f.properties.id
                if (e.preventDefault) e.preventDefault()
                focusOnLocation(locationId, 'places' as const)
              }}
            >
              <div className="locationbookdetails">
                <div className="locationbookimage">
                  <img src={authorImageURL} />
                </div>
                <div className="locationbookreference">
                  <div className="locationbooktitle">{bookData?.bookName}</div>
                  {matchingName ? (
                    ''
                  ) : (
                    <div className="locationtitle">{f.properties.name}</div>
                  )}
                  {author && <div className="locationbookauthor">{author}</div>}
                </div>
              </div>
              <div className="locationbooklink">
                <a
                  onClick={() => viewSelectedPlace(f.properties.id)}
                  className="btn-pill"
                  href="#"
                >
                  View
                </a>
              </div>
            </div>
          )
        })}
      </div>
    </Popup>
  )
}

export default LocationPopup
